import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, Clock, Twitter, Facebook, Linkedin, ArrowLeft } from 'lucide-react';
import { blogPosts } from '../content/blogPosts';
import { PageHeader, PageFooter } from '../components/PageLayout';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogPosts[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Helmet>
          <title>Article non trouvé | DJ Nomad Blog</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <PageHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-6">📄</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Article non trouvé</h1>
            <button
              onClick={() => navigate('/blog')}
              className="text-violet-600 hover:underline font-semibold"
            >
              Retour au blog
            </button>
          </div>
        </div>
        <PageFooter />
      </div>
    );
  }

  const shareUrl = `https://djnomad.app/blog/${slug}`;
  const shareTitle = encodeURIComponent(post.title);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{post.title} | DJ Nomad Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={post.keywords?.join(', ') || ''} />
        <meta name="author" content={post.author || 'DJ Nomad Team'} />
        <link rel="canonical" href={shareUrl} />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content="https://djnomad.app/og-image-new.png" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author || 'DJ Nomad Team'} />
        <meta property="article:section" content={post.category} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={shareUrl} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content="https://djnomad.app/og-image-new.png" />
        
        {/* JSON-LD Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt,
            "datePublished": post.date,
            "author": {
              "@type": "Organization",
              "name": post.author || "DJ Nomad Team"
            },
            "publisher": {
              "@type": "Organization",
              "name": "DJ Nomad",
              "logo": {
                "@type": "ImageObject",
                "url": "https://djnomad.app/icon-512.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": shareUrl
            },
            "keywords": post.keywords?.join(', ') || ''
          })}
        </script>
      </Helmet>
      
      {/* Header */}
      <PageHeader />

      {/* Article */}
      <article className="max-w-4xl mx-auto px-6 py-12 flex-1">
        {/* Back Button */}
        <button
          onClick={() => navigate('/blog')}
          className="flex items-center gap-2 text-gray-600 hover:text-violet-600 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Retour au blog</span>
        </button>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-gray-500">
          <span className="px-3 py-1 bg-violet-100 border border-violet-200 rounded-full text-violet-600 font-medium">
            {post.category}
          </span>
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            <span>{post.date}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{post.readTime}</span>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          {post.title}
        </h1>

        {/* Excerpt */}
        <p className="text-xl text-gray-600 mb-8 leading-relaxed">
          {post.excerpt}
        </p>

        {/* Share Buttons */}
        <div className="flex items-center gap-3 mb-12 pb-8 border-b border-gray-200">
          <span className="text-gray-500 text-sm">Partager :</span>
          <a
            href={`https://twitter.com/intent/tweet?text=${shareTitle}&url=${shareUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 flex items-center justify-center bg-sky-50 hover:bg-sky-100 border border-sky-200 rounded-lg transition-colors"
          >
            <Twitter className="w-5 h-5 text-sky-500" />
          </a>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 flex items-center justify-center bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
          >
            <Facebook className="w-5 h-5 text-blue-600" />
          </a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 flex items-center justify-center bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
          >
            <Linkedin className="w-5 h-5 text-blue-700" />
          </a>
        </div>

        {/* Content */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-10 shadow-sm">
          <div 
            className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-a:text-violet-600 prose-strong:text-gray-900 prose-ul:text-gray-600 prose-ol:text-gray-600"
            dangerouslySetInnerHTML={{ __html: post.content }} 
          />
        </div>

        {/* CTA */}
        <div className="mt-12 p-8 bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-2xl text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Prêt à automatiser la musique de vos événements ?
          </h3>
          <p className="text-gray-600 mb-6">
            Essayez DJ Nomad gratuitement et découvrez la simplicité du mode offline-first.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-lg font-bold rounded-xl transition-all duration-300 transform hover:scale-105"
          >
            Commencer Gratuitement
          </button>
        </div>
      </article>

      {/* Footer */}
      <PageFooter />
    </div>
  );
};

export default BlogPost;